import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CheckCircle2 } from "lucide-react";
import { processData } from "../../../../data/howItWorks/processData";
import "./PerformanceModel.css";

gsap.registerPlugin(ScrollTrigger);

const PerformanceModel = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate header
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      // Animate stats
      const stats = statsRef.current.querySelectorAll(".performance-stat");
      gsap.fromTo(
        stats,
        { opacity: 0, scale: 0.8, y: 40 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: statsRef.current,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      // Animate features
      const features = featuresRef.current.querySelectorAll(
        ".performance-feature",
      );
      gsap.fromTo(
        features,
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: featuresRef.current,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="performance-model" ref={sectionRef}>
      <div className="performance-model__container">
        <div className="performance-model__header" ref={headerRef}>
          <div className="performance-model__badge">
            {processData.performanceModel.badge}
          </div>
          <h2 className="performance-model__headline">
            {processData.performanceModel.headline}
          </h2>
          <p className="performance-model__description">
            {processData.performanceModel.description}
          </p>
        </div>

        <div className="performance-model__stats" ref={statsRef}>
          {processData.performanceModel.stats.map((stat, index) => (
            <div key={index} className="performance-stat">
              <div className="performance-stat__number">{stat.number}</div>
              <div className="performance-stat__label">{stat.label}</div>
              <div className="performance-stat__description">
                {stat.description}
              </div>
            </div>
          ))}
        </div>

        <div className="performance-model__features" ref={featuresRef}>
          <h3 className="performance-model__features-title">
            What This Means For You:
          </h3>
          <ul className="performance-model__features-list">
            {processData.performanceModel.features.map((feature, index) => (
              <li key={index} className="performance-feature">
                <CheckCircle2 size={24} strokeWidth={2.5} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default PerformanceModel;
